'use client'

import { Fragment, useContext, useEffect } from 'react'
import { ArticleAreaSequenceResponseVo, ArticleResponseVo } from '@schemas/non-auth'
import { pxToRem } from '@ui/style/muiTheme'
import UIFlex from '@components/ui/UIFlex'
import CommentBox, { CommentBoxProps } from '@components/content/CommentBox'
import { ArticleRefContext } from '@page/article/_components/common/ArticleRefContext'
import SiblingArticles, { SiblingArticlesProps } from './SiblingArticles'
import RelatedArticleList, { RelatedArticleListProps } from './RelatedArticleList'
import Article from './Article'
import AuthorSlide, { AuthorSlideProps } from './AuthorSlide'
import Banner from './ArticleBanner'
import RecommendButtons from './RecommendButtons'
import SubTitle from './SubTitle'
import TagList from './TagList'
import NoComment from '@components/common/Comments/NoComment'
import { useSetRecoilState } from 'recoil'
import { PlayingVideoState, playingVideoState } from '@store/playingVideo'
import isFailedAPI from '@utils/isFailedAPI'
import BodyImgPcAd from '@components/ads/Article/Body/BodyImgPc'
import { Box } from '@mui/joy'
import ShoppingLife from '@components/ads/Article/ShoppingLife'
import BottomAd from '@components/ads/Article/Bottom'
import { useIsAppControl } from '@store/isApp'
import BodyMixAd from '@components/ads/Article/Body/BodyMix'
import AsideAdWrapper from '@components/ads/Article/Aside/AsideAdWrapper'
import { theme } from '@ui/style'
import useIframeZoom from '@hooks/useIframeZoom'
import { Taboola } from '@components/ads/Article/Taboola'
import { TextBottomAd } from '@components/ads/Article/TextAds'
import { useMedia } from '@hooks/useMedia'

type ArticleBodyProps = Omit<
	RelatedArticleListProps &
		SiblingArticlesProps &
		AuthorSlideProps &
		Omit<CommentBoxProps, 'contentId' | 'contentDiv'>,
	'query'
> & {
	areaSeqList: Array<ArticleAreaSequenceResponseVo>
	article: ArticleResponseVo
	commentVisible: boolean
	query?: {
		code?: string
		idx?: string
	}
}
const ArticleBody = ({
	areaSeqList,
	article,
	journalist,
	relatedArticle,
	preNext,
	query,
	commentVisible,
}: ArticleBodyProps) => {
	const { isApp } = useIsAppControl()
	const articleRef = useContext(ArticleRefContext)
	const setPlayingVideo = useSetRecoilState<PlayingVideoState>(playingVideoState)
	const isRelatedNews = areaSeqList.find((area) => area.articleAreaCode.code === 'RELATED_NEWS')
	const { iframeStyles } = useIframeZoom()
	const isMediaSM = useMedia('sm')

	const switchArticleBody = (item: ArticleAreaSequenceResponseVo) => {
		const areaCode = item.articleAreaCode.code
		switch (areaCode) {
			case 'ARTICLE_DETAILS':
				return (
					<Fragment key={`article-detail-${item.areaIdx}`}>
						<UIFlex
							ref={articleRef.bodyRef}
							sx={{
								'&>*:not(:first-child)': {
									marginTop: {
										xs: pxToRem(40),
										md: pxToRem(48),
									},
								},
								'& > .adtssp_mo': {
									display: { xs: 'block', lg: 'none' },
									[theme.mediaQueries.under.origin]: {
										'&>div': {
											width: `${pxToRem(353)}!important`,
											height: `${pxToRem(111)}!important`,
											overflow: 'hidden',
											'&>iframe': {
												...iframeStyles,
												position: 'static!important',
											},
										},
									},
									'&:first-child + .adtssp_pc': {
										marginTop: 0,
									},
								},
								'& > .adtssp_pc': {
									display: { xs: 'none', lg: 'block' },
								},
							}}
						>
							{article.articleSubTitle && (
								<SubTitle>{article.articleSubTitle}</SubTitle>
							)}
							<Article article={article} />
							{(article?.tagList?.length ?? 0) > 0 && (
								<TagList tagList={article.tagList} />
							)}
							{!isApp && (
								<UIFlex
									className="print-hide"
									sx={{
										flexDirection: { xs: 'column', md: 'row' },
										justifyContent: 'space-between',
									}}
								>
									<TextBottomAd />
									<BodyImgPcAd />
								</UIFlex>
							)}
							{journalist &&
								!isFailedAPI(journalist) &&
								(journalist?.length ?? 0) > 0 && (
									<AuthorSlide journalist={journalist} />
								)}
						</UIFlex>
					</Fragment>
				)
			case 'NEWS_SYMPATHY':
				return (
					<RecommendButtons
						key={`recommend-buttons-${item.areaIdx}`}
						articleIdx={article.articleIdx}
						articleLikeCnt={article.articleLikeCnt}
						articleDislikeCnt={article.articleDislikeCnt}
						isLike={article.isLike ?? false}
						isDislike={article.isDislike ?? false}
						areaIdx={item.areaIdx}
					/>
				)
			case 'COMMENT':
				return (
					<Fragment key={`comment-${item.areaIdx}`}>
						{commentVisible ? (
							<CommentBox contentId={article.articleIdx} contentDiv="NEWS" />
						) : (
							<NoComment targetName="기사" />
						)}
						{!isApp ? (
							<>
								<Taboola type="feedA" />
								<Taboola type="feedB" />
								{!isRelatedNews && <ShoppingLife />}
								<Box
									className="print-hide"
									display={{
										xs: 'block',
										md: 'none',
									}}
								>
									{/* 혼합 */}
									<BodyMixAd />
									{!isRelatedNews && <Taboola type="feedC" />}
									<AsideAdWrapper>
										<BottomAd index={0} />
										<BottomAd index={1} />
									</AsideAdWrapper>
								</Box>
							</>
						) : (
							<>
								<Taboola type="issue" />
								{!isRelatedNews && <Taboola type="feedC" />}
							</>
						)}
					</Fragment>
				)
			case 'RELATED_NEWS':
				return (
					<Fragment key={`comment-${item.areaIdx}`}>
						<Taboola type="feedC" />
						{relatedArticle &&
							!isFailedAPI(relatedArticle) &&
							relatedArticle.length > 0 && (
								<RelatedArticleList
									key={`related-news-${item.areaIdx}`}
									relatedArticle={relatedArticle}
									query={undefined}
									areaIdx={item.areaIdx}
								/>
							)}
						{!isApp && <ShoppingLife />}
					</Fragment>
				)
			case 'BANNER':
				return <Banner key={`banner-${item.areaIdx}`} />
			case 'NEWS_PRENEXT':
				return (
					preNext &&
					!isFailedAPI(preNext) && (
						<SiblingArticles
							key={`news-prenext-${item.areaIdx}`}
							preNext={preNext}
							query={query}
							areaIdx={item.areaIdx}
						/>
					)
				)
			default:
				return <Fragment key={`etc-${item.areaIdx}`} />
		}
	}
	useEffect(() => {
		return () => {
			setPlayingVideo({ videoIdx: null })
		}
	}, [])
	return (
		<UIFlex
			sx={{
				'&>*:not(:first-child)': {
					marginTop: {
						xs: pxToRem(40),
						md: pxToRem(48),
					},
				},
			}}
		>
			{areaSeqList.map(switchArticleBody)}
			{!isApp && !isMediaSM && <Taboola type="scroll" />}
		</UIFlex>
	)
}

export default ArticleBody
